/**
 * @file index.tsx
 *
 * @fileoverview Renders a slider component.
 */
import React, { useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import styles from "./slider.module.scss";
import { cva } from "class-variance-authority";
import ArrowRightIcon from "@jq-icons/ArrowRight";
import ArrowLeftIcon from "@jq-icons/ArrowLeft";
import { useState } from "react";
import PauseIcon from "@jq-icons/Pause";
import PlayIcon from "@jq-icons/Play";

type SliderCommonProps = {
  /**
   * Amount of slides per view.
   *
   * @default "1"
   */
  slidesToShow?: number | "auto";
  /**
   * The amount of slides to scroll at each time.
   *
   * @default "1"
   */
  slidesToScroll?: number;
  /**
   * Show dots navigation.
   */
  dots?: boolean;
};

type SlideProps = SliderCommonProps & {
  /**
   * The slides to show inside the slider.
   */
  slides: React.ReactNode[];
  /**
   * Infinite loop sliding.
   *
   * @default "true"
   */
  infinite?: boolean;
  /**
   * Show slider navigation arrows.
   *
   * @default "true"
   */
  showArrows?: boolean;
  /**
   * Responsive settings for the slider.
   */
  responsive?: {
    breakpoint: number;
    settings: SliderCommonProps;
  }[];
  /**
   * If true, all slides will have the same height.
   */
  equalHeight?: boolean;
  /**
   * If true, the slider will autoplay.
   */
  autoplay?: boolean;
  /**
   * The speed of the autoplay in milliseconds.
   */
  autoplaySpeed?: number;
  /**
   * If true, the play/pause button will be shown when autoplay is active.
   */
  showPlayPauseButton?: boolean;
  /**
   * Center mode for the slider.
   *
   * @default "false"
   */
  centerMode?: boolean;
  /**
   * If true, the slider will use a fade effect instead of sliding.
   *
   * @default "false"
   */
  fade?: boolean;
};

const slider = cva([styles.slider], {
  variants: {
    showArrows: {
      true: styles.showArrows,
    },
    equalHeight: {
      true: styles.equalHeight,
    },
  },
});

function NextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <ArrowRightIcon
      color="gray6"
      className={className}
      style={{ ...style, display: "block" }}
      onClick={onClick}
    />
  );
}

function PrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <ArrowLeftIcon
      color="gray6"
      className={className}
      style={{ ...style, display: "block" }}
      onClick={onClick}
    />
  );
}

const PlayPauseButton = ({ isPlaying, onClick }) => (
  <button onClick={onClick} className={styles.playPauseButton}>
    {isPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
  </button>
);

/**
 * A slider component should be used when we want to display several items and
 * save space. However, there are UX and accessibility concerns when using
 * a slider since many of the items will be hidden from the user by default
 * (needs clicking arrows to see them).
 */
export default function Slide({
  slides,
  slidesToShow = 1,
  slidesToScroll = 1,
  infinite = true,
  showArrows = true,
  responsive,
  equalHeight = true,
  autoplay = false,
  autoplaySpeed = 2000,
  showPlayPauseButton = true,
  dots = true,
  centerMode = false,
  fade = false,
}: SlideProps) {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const sliderRef = useRef<Slider>(null);

  const settings = {
    dots: dots,
    speed: 500,
    infinite: infinite,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    arrows: showArrows,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    autoplay: isPlaying,
    autoplaySpeed: autoplaySpeed,
    showPlayPauseButton: showPlayPauseButton,
    responsive,
    centerMode: centerMode,
    fade: fade,
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      sliderRef.current.slickPause();
    } else {
      sliderRef.current.slickPlay();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <>
      <Slider
        ref={sliderRef}
        className={slider({ showArrows, equalHeight: fade ? false : equalHeight })}
        {...settings}
      >
        {slides.map((slide, index) => (
          <div className={styles.slideElementWrapper} key={index}>
            {slide}
          </div>
        ))}
      </Slider>
      {autoplay && showPlayPauseButton && (
        <div className={styles.playPauseButtonWrapper}>
          <PlayPauseButton isPlaying={isPlaying} onClick={togglePlayPause} />
        </div>
      )}
    </>
  );
}

export { Slide };
